import type React from 'react';
import { MessagesSquare } from 'lucide-react';
import { Link } from 'react-router';

import classes from '../Chat.module.scss';
import type { ChatRole } from '../types';
import { formatLastActivity } from '../utils/formatLastActivity';
import { formatResponseStatusLabel } from '../utils/formatBulletinLabel';
import { BrandLoader } from 'ui/BrandLoader/BrandLoader';

export interface BulletinThreadItem {
  bulletin_id: number;
  label: string;
  source?: 'response' | 'invitation';
  status?: string;
  unread_count: number;
  chat_count: number;
  tutor_count?: number;
  last_activity?: string | null;
}

interface BulletinThreadListProps {
  threads: BulletinThreadItem[];
  role: ChatRole;
  selectedBulletinId: number | null;
  isLoading: boolean;
  onSelect: (bulletinId: number) => void;
  countMode?: 'chats' | 'tutors';
  embedded?: boolean;
}

const formatCountLabel = (count: number, mode: 'chats' | 'tutors'): string => {
  if (mode === 'tutors') {
    if (count === 1) return '1 репетитор';
    if (count > 1 && count < 5) return `${count} репетитора`;
    return `${count} репетиторов`;
  }
  if (count === 1) return '1 чат';
  if (count > 1 && count < 5) return `${count} чата`;
  return `${count} чатов`;
};

export const BulletinThreadList: React.FC<BulletinThreadListProps> = ({
  threads,
  role,
  selectedBulletinId,
  isLoading,
  onSelect,
  countMode = 'chats',
  embedded = false,
}) => {
  const sidebarClass =
    role === 'tutor'
      ? classes['chat-list-sidebar--tutor']
      : classes['chat-list-sidebar--student'];

  const title = role === 'tutor' ? 'Заявки' : 'Мои заявки';
  const rootClass = embedded
    ? `${classes['chat-bulletin-panel']} ${sidebarClass}`
    : `${classes['chat-list-sidebar']} ${classes['chat-bulletin-sidebar']} ${sidebarClass}`;

  if (isLoading) {
    return (
      <div className={rootClass}>
        {!embedded ? <h2>{title}</h2> : null}
        <div className={classes['chat-list-loading']}>
          <BrandLoader center label='Загрузка...' />
        </div>
      </div>
    );
  }

  if (threads.length === 0) {
    return (
      <div className={rootClass}>
        {!embedded ? <h2>{title}</h2> : null}
        {/* Пустое состояние 54:12266: иконка, факт и действие вместо одной строки на пустой карточке. */}
        <div className={`${classes['chat-list-empty']} ${classes['chat-list-empty--state']}`}>
          <span className={classes['chat-list-empty__art']} aria-hidden>
            <MessagesSquare size={40} strokeWidth={1.5} />
          </span>
          <p>Пока нет диалогов</p>
          <small>
            {role === 'tutor'
              ? 'Диалоги появятся, когда вы откликнетесь на заявку или ученик пригласит вас.'
              : 'Диалоги с репетиторами появятся после отклика на вашу задачу или приглашения.'}
          </small>
          <Link
            className={classes['chat-list-empty__action']}
            to={role === 'tutor' ? '/teacher/search' : '/profile/tasks'}
          >
            {role === 'tutor' ? 'Найти заявки' : 'К моим задачам'}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={rootClass}>
      {!embedded ? <h2>{title}</h2> : null}
      <div className={classes['chat-list-items']}>
        {threads.map((thread) => {
          const isSelected = selectedBulletinId === thread.bulletin_id;
          const activeClass =
            role === 'tutor'
              ? classes['chat-list-item--active-tutor']
              : classes['chat-list-item--active'];
          const displayCount =
            countMode === 'tutors'
              ? thread.tutor_count ?? thread.chat_count
              : thread.chat_count;
          const activityLabel = formatLastActivity(thread.last_activity);

          return (
            <div
              key={thread.bulletin_id}
              className={`${classes['chat-list-item']} ${isSelected ? activeClass : ''}`}
              onClick={() => onSelect(thread.bulletin_id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  onSelect(thread.bulletin_id);
                }
              }}
              role='button'
              tabIndex={0}
            >
              <div className={classes['chat-list-content']}>
                <div className={classes['chat-list-header']}>
                  <h4 className={classes['chat-list-name']} title={thread.label}>
                    {thread.label}
                  </h4>
                  <div className={classes['chat-list-meta']}>
                    {activityLabel ? (
                      <span className={classes['chat-list-time']}>{activityLabel}</span>
                    ) : null}
                    {displayCount > 0 ? (
                      <span className={classes['chat-list-bulletin-count']}>
                        {formatCountLabel(displayCount, countMode)}
                      </span>
                    ) : null}
                  </div>
                </div>
                {thread.source === 'invitation' ? (
                  <p className={classes['chat-list-bulletin']}>Приглашение от ученика</p>
                ) : thread.status ? (
                  <p className={classes['chat-list-bulletin']}>
                    {formatResponseStatusLabel(thread.status)}
                  </p>
                ) : null}
              </div>
              {thread.unread_count > 0 ? (
                <div className={classes['unread-badge']}>
                  {thread.unread_count > 9 ? '9+' : thread.unread_count}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};
